import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge, Form, ButtonGroup, Button, Row, Col } from 'react-bootstrap';
import { FaFilter, FaSearch, FaCircle } from 'react-icons/fa';

interface S3Event {
  id: string;
  timestamp: Date;
  bucket: string;
  key: string;
  size: number;
  eventType: 'PUT' | 'DELETE' | 'COPY' | 'POST';
  agent: string;
  workflow?: string;
  region: string;
}

interface S3EventStatistics {
  totalEvents: number;
  eventsPerMinute: number;
  topAgents: { agent: string; count: number }[];
  eventTypeBreakdown: Record<string, number>;
}

interface EventFilter {
  agent?: string;
  eventType?: string;
  workflow?: string;
  search?: string;
}

interface S3EventStreamProps {
  wsConnection: WebSocket | null;
}

const S3EventStream: React.FC<S3EventStreamProps> = ({ wsConnection }) => {
  const [events, setEvents] = useState<S3Event[]>([]);
  const [statistics, setStatistics] = useState<S3EventStatistics | null>(null);
  const [filter, setFilter] = useState<EventFilter>({});
  const [autoScroll, setAutoScroll] = useState(true);
  const eventListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wsConnection) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 's3_event') {
          setEvents(prev => {
            const newEvents = [data.data, ...prev].slice(0, 100);
            return newEvents;
          });
        } else if (data.type === 's3_statistics') {
          setStatistics(data.data);
        }
      } catch (error) {
        console.error('Error processing S3 event:', error);
      }
    };

    wsConnection.addEventListener('message', handleMessage);
    
    // Request recent events only if connected
    if (wsConnection.readyState === WebSocket.OPEN) {
      wsConnection.send(JSON.stringify({
        type: 'get_recent_s3_events',
        limit: 50
      }));
    } else {
      // Wait for connection to open
      const handleOpen = () => {
        wsConnection.send(JSON.stringify({
          type: 'get_recent_s3_events',
          limit: 50
        }));
        wsConnection.removeEventListener('open', handleOpen);
      };
      wsConnection.addEventListener('open', handleOpen);
    }

    return () => {
      wsConnection.removeEventListener('message', handleMessage);
    };
  }, [wsConnection]);

  useEffect(() => {
    if (autoScroll && eventListRef.current) {
      eventListRef.current.scrollTop = 0;
    }
  }, [events, autoScroll]);

  const filteredEvents = events.filter(event => {
    if (filter.agent && !event.agent.toLowerCase().includes(filter.agent.toLowerCase())) {
      return false;
    }
    if (filter.eventType && event.eventType !== filter.eventType) {
      return false;
    }
    if (filter.workflow && !event.workflow?.toLowerCase().includes(filter.workflow.toLowerCase())) {
      return false;
    }
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return event.key.toLowerCase().includes(searchLower) ||
             event.agent.toLowerCase().includes(searchLower) ||
             (event.workflow?.toLowerCase().includes(searchLower) ?? false);
    }
    return true;
  });

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'PUT': return 'success';
      case 'DELETE': return 'danger';
      case 'COPY': return 'info';
      case 'POST': return 'primary';
      default: return 'secondary';
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatKey = (key: string) => {
    const parts = key.split('/');
    if (parts.length > 3) {
      return `.../${parts.slice(-2).join('/')}`;
    }
    return key;
  };

  return (
    <Card className="h-100">
      <Card.Header className="bg-dark text-white">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaCircle className="text-success me-2" size={10} />
            S3 Event Stream
          </h5>
          <div className="d-flex align-items-center gap-2">
            {statistics && (
              <Badge bg="info">{statistics.eventsPerMinute} events/min</Badge>
            )}
            <Button
              size="sm"
              variant={autoScroll ? 'primary' : 'secondary'}
              onClick={() => setAutoScroll(!autoScroll)}
            >
              Auto-scroll: {autoScroll ? 'ON' : 'OFF'}
            </Button>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-2">
        <Row className="mb-2">
          <Col md={3}>
            <Form.Control
              size="sm"
              placeholder="Filter by agent..."
              value={filter.agent || ''}
              onChange={(e) => setFilter({ ...filter, agent: e.target.value })}
            />
          </Col>
          <Col md={3}>
            <Form.Select
              size="sm"
              value={filter.eventType || ''}
              onChange={(e) => setFilter({ ...filter, eventType: e.target.value })}
            >
              <option value="">All Event Types</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
              <option value="COPY">COPY</option>
              <option value="POST">POST</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Control
              size="sm"
              placeholder="Filter by workflow..."
              value={filter.workflow || ''}
              onChange={(e) => setFilter({ ...filter, workflow: e.target.value })}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              size="sm"
              placeholder="Search..."
              value={filter.search || ''}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
          </Col>
        </Row>

        <div 
          ref={eventListRef}
          style={{ 
            height: '400px', 
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: '#1a1a1a',
            borderRadius: '4px',
            padding: '8px'
          }}
        >
          {filteredEvents.length === 0 ? (
            <div className="text-center text-muted py-5">
              No events matching filters
            </div>
          ) : (
            filteredEvents.map(event => (
              <div 
                key={event.id} 
                className="mb-2 p-2"
                style={{ 
                  backgroundColor: '#2a2a2a',
                  borderRadius: '4px',
                  borderLeft: `3px solid var(--bs-${getEventTypeColor(event.eventType)})`,
                  fontSize: '0.85rem'
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Badge bg={getEventTypeColor(event.eventType)} style={{ minWidth: '60px' }}>
                        {event.eventType}
                      </Badge>
                      <Badge bg="dark">{event.agent}</Badge>
                      {event.workflow && (
                        <Badge bg="secondary">{event.workflow}</Badge>
                      )}
                      <span className="text-muted ms-auto">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-light" style={{ fontFamily: 'monospace' }}>
                      {formatKey(event.key)}
                    </div>
                  </div>
                  <div className="text-muted ms-2">
                    {formatSize(event.size)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {statistics && (
          <div className="mt-3 pt-3 border-top">
            <Row>
              <Col md={6}>
                <h6>Top Active Agents</h6>
                <div className="d-flex flex-column gap-1">
                  {statistics.topAgents.slice(0, 5).map(({ agent, count }) => (
                    <div key={agent} className="d-flex justify-content-between">
                      <span className="text-muted">{agent}</span>
                      <Badge bg="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </Col>
              <Col md={6}>
                <h6>Event Type Distribution</h6>
                <div className="d-flex flex-column gap-1">
                  {Object.entries(statistics.eventTypeBreakdown).map(([type, count]) => (
                    <div key={type} className="d-flex justify-content-between">
                      <span className="text-muted">{type}</span>
                      <Badge bg={getEventTypeColor(type)}>{count}</Badge>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default S3EventStream;